/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressEncoder,
  getArrayDecoder,
  getArrayEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  getU64Decoder,
  getU64Encoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type Codec,
  type Decoder,
  type Encoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
  type WritableSignerAccount,
} from '@solana/kit';
import {
  getAccountMetaFactory,
  getAddressFromResolvedInstructionAccount,
  type ResolvedInstructionAccount,
} from '@solana/program-client-core';
import { DOPPLER_VESTING_PROGRAM_ADDRESS } from '../programs';
import {
  getVestingAllocationInputDecoder,
  getVestingAllocationInputEncoder,
  getVestingScheduleDecoder,
  getVestingScheduleEncoder,
  type VestingAllocationInput,
  type VestingAllocationInputArgs,
  type VestingSchedule,
  type VestingScheduleArgs,
} from '../types';

export const INITIALIZE_VESTING_DISCRIMINATOR = new Uint8Array([
  5, 29, 245, 237, 50, 242, 35, 13,
]);

export function getInitializeVestingDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    INITIALIZE_VESTING_DISCRIMINATOR,
  );
}

export type InitializeVestingInstruction<
  TProgram extends string = typeof DOPPLER_VESTING_PROGRAM_ADDRESS,
  TAccountPayer extends string | AccountMeta<string> = string,
  TAccountLaunch extends string | AccountMeta<string> = string,
  TAccountBaseMint extends string | AccountMeta<string> = string,
  TAccountBaseTokenProgram extends string | AccountMeta<string> = string,
  TAccountVestingConfig extends string | AccountMeta<string> = string,
  TAccountSystemProgram extends string | AccountMeta<string> =
    '11111111111111111111111111111111',
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountPayer extends string
        ? WritableSignerAccount<TAccountPayer> &
            AccountSignerMeta<TAccountPayer>
        : TAccountPayer,
      TAccountLaunch extends string
        ? ReadonlyAccount<TAccountLaunch>
        : TAccountLaunch,
      TAccountBaseMint extends string
        ? ReadonlySignerAccount<TAccountBaseMint> &
            AccountSignerMeta<TAccountBaseMint>
        : TAccountBaseMint,
      TAccountBaseTokenProgram extends string
        ? ReadonlyAccount<TAccountBaseTokenProgram>
        : TAccountBaseTokenProgram,
      TAccountVestingConfig extends string
        ? WritableAccount<TAccountVestingConfig>
        : TAccountVestingConfig,
      TAccountSystemProgram extends string
        ? ReadonlyAccount<TAccountSystemProgram>
        : TAccountSystemProgram,
      ...TRemainingAccounts,
    ]
  >;

export type InitializeVestingInstructionData = {
  discriminator: ReadonlyUint8Array;
  initialSupply: bigint;
  schedules: Array<VestingSchedule>;
  allocations: Array<VestingAllocationInput>;
};

export type InitializeVestingInstructionDataArgs = {
  initialSupply: number | bigint;
  schedules: Array<VestingScheduleArgs>;
  allocations: Array<VestingAllocationInputArgs>;
};

export function getInitializeVestingInstructionDataEncoder(): Encoder<InitializeVestingInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['initialSupply', getU64Encoder()],
      ['schedules', getArrayEncoder(getVestingScheduleEncoder())],
      ['allocations', getArrayEncoder(getVestingAllocationInputEncoder())],
    ]),
    (value) => ({ ...value, discriminator: INITIALIZE_VESTING_DISCRIMINATOR }),
  );
}

export function getInitializeVestingInstructionDataDecoder(): Decoder<InitializeVestingInstructionData> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['initialSupply', getU64Decoder()],
    ['schedules', getArrayDecoder(getVestingScheduleDecoder())],
    ['allocations', getArrayDecoder(getVestingAllocationInputDecoder())],
  ]);
}

export function getInitializeVestingInstructionDataCodec(): Codec<
  InitializeVestingInstructionDataArgs,
  InitializeVestingInstructionData
> {
  return combineCodec(
    getInitializeVestingInstructionDataEncoder(),
    getInitializeVestingInstructionDataDecoder(),
  );
}

export type InitializeVestingAsyncInput<
  TAccountPayer extends string = string,
  TAccountLaunch extends string = string,
  TAccountBaseMint extends string = string,
  TAccountBaseTokenProgram extends string = string,
  TAccountVestingConfig extends string = string,
  TAccountSystemProgram extends string = string,
> = {
  payer: TransactionSigner<TAccountPayer>;
  launch: Address<TAccountLaunch>;
  /** Future launch mint. Its signature prevents another caller from claiming this config PDA. */
  baseMint: TransactionSigner<TAccountBaseMint>;
  baseTokenProgram: Address<TAccountBaseTokenProgram>;
  vestingConfig?: Address<TAccountVestingConfig>;
  systemProgram?: Address<TAccountSystemProgram>;
  initialSupply: InitializeVestingInstructionDataArgs['initialSupply'];
  schedules: InitializeVestingInstructionDataArgs['schedules'];
  allocations: InitializeVestingInstructionDataArgs['allocations'];
};

export async function getInitializeVestingInstructionAsync<
  TAccountPayer extends string,
  TAccountLaunch extends string,
  TAccountBaseMint extends string,
  TAccountBaseTokenProgram extends string,
  TAccountVestingConfig extends string,
  TAccountSystemProgram extends string,
  TProgramAddress extends Address = typeof DOPPLER_VESTING_PROGRAM_ADDRESS,
>(
  input: InitializeVestingAsyncInput<
    TAccountPayer,
    TAccountLaunch,
    TAccountBaseMint,
    TAccountBaseTokenProgram,
    TAccountVestingConfig,
    TAccountSystemProgram
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  InitializeVestingInstruction<
    TProgramAddress,
    TAccountPayer,
    TAccountLaunch,
    TAccountBaseMint,
    TAccountBaseTokenProgram,
    TAccountVestingConfig,
    TAccountSystemProgram
  >
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? DOPPLER_VESTING_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    payer: { value: input.payer ?? null, isWritable: true },
    launch: { value: input.launch ?? null, isWritable: false },
    baseMint: { value: input.baseMint ?? null, isWritable: false },
    baseTokenProgram: {
      value: input.baseTokenProgram ?? null,
      isWritable: false,
    },
    vestingConfig: { value: input.vestingConfig ?? null, isWritable: true },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.vestingConfig.value) {
    accounts.vestingConfig.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([
            118, 101, 115, 116, 105, 110, 103, 95, 99, 111, 110, 102, 105, 103,
          ]),
        ),
        getAddressEncoder().encode(
          getAddressFromResolvedInstructionAccount(
            'launch',
            accounts.launch.value,
          ),
        ),
        getAddressEncoder().encode(
          getAddressFromResolvedInstructionAccount(
            'baseMint',
            accounts.baseMint.value,
          ),
        ),
      ],
    });
  }
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      '11111111111111111111111111111111' as Address<'11111111111111111111111111111111'>;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('payer', accounts.payer),
      getAccountMeta('launch', accounts.launch),
      getAccountMeta('baseMint', accounts.baseMint),
      getAccountMeta('baseTokenProgram', accounts.baseTokenProgram),
      getAccountMeta('vestingConfig', accounts.vestingConfig),
      getAccountMeta('systemProgram', accounts.systemProgram),
    ],
    data: getInitializeVestingInstructionDataEncoder().encode(
      args as InitializeVestingInstructionDataArgs,
    ),
    programAddress,
  } as InitializeVestingInstruction<
    TProgramAddress,
    TAccountPayer,
    TAccountLaunch,
    TAccountBaseMint,
    TAccountBaseTokenProgram,
    TAccountVestingConfig,
    TAccountSystemProgram
  >);
}

export type InitializeVestingInput<
  TAccountPayer extends string = string,
  TAccountLaunch extends string = string,
  TAccountBaseMint extends string = string,
  TAccountBaseTokenProgram extends string = string,
  TAccountVestingConfig extends string = string,
  TAccountSystemProgram extends string = string,
> = {
  payer: TransactionSigner<TAccountPayer>;
  launch: Address<TAccountLaunch>;
  /** Future launch mint. Its signature prevents another caller from claiming this config PDA. */
  baseMint: TransactionSigner<TAccountBaseMint>;
  baseTokenProgram: Address<TAccountBaseTokenProgram>;
  vestingConfig: Address<TAccountVestingConfig>;
  systemProgram?: Address<TAccountSystemProgram>;
  initialSupply: InitializeVestingInstructionDataArgs['initialSupply'];
  schedules: InitializeVestingInstructionDataArgs['schedules'];
  allocations: InitializeVestingInstructionDataArgs['allocations'];
};

export function getInitializeVestingInstruction<
  TAccountPayer extends string,
  TAccountLaunch extends string,
  TAccountBaseMint extends string,
  TAccountBaseTokenProgram extends string,
  TAccountVestingConfig extends string,
  TAccountSystemProgram extends string,
  TProgramAddress extends Address = typeof DOPPLER_VESTING_PROGRAM_ADDRESS,
>(
  input: InitializeVestingInput<
    TAccountPayer,
    TAccountLaunch,
    TAccountBaseMint,
    TAccountBaseTokenProgram,
    TAccountVestingConfig,
    TAccountSystemProgram
  >,
  config?: { programAddress?: TProgramAddress },
): InitializeVestingInstruction<
  TProgramAddress,
  TAccountPayer,
  TAccountLaunch,
  TAccountBaseMint,
  TAccountBaseTokenProgram,
  TAccountVestingConfig,
  TAccountSystemProgram
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? DOPPLER_VESTING_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    payer: { value: input.payer ?? null, isWritable: true },
    launch: { value: input.launch ?? null, isWritable: false },
    baseMint: { value: input.baseMint ?? null, isWritable: false },
    baseTokenProgram: {
      value: input.baseTokenProgram ?? null,
      isWritable: false,
    },
    vestingConfig: { value: input.vestingConfig ?? null, isWritable: true },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      '11111111111111111111111111111111' as Address<'11111111111111111111111111111111'>;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('payer', accounts.payer),
      getAccountMeta('launch', accounts.launch),
      getAccountMeta('baseMint', accounts.baseMint),
      getAccountMeta('baseTokenProgram', accounts.baseTokenProgram),
      getAccountMeta('vestingConfig', accounts.vestingConfig),
      getAccountMeta('systemProgram', accounts.systemProgram),
    ],
    data: getInitializeVestingInstructionDataEncoder().encode(
      args as InitializeVestingInstructionDataArgs,
    ),
    programAddress,
  } as InitializeVestingInstruction<
    TProgramAddress,
    TAccountPayer,
    TAccountLaunch,
    TAccountBaseMint,
    TAccountBaseTokenProgram,
    TAccountVestingConfig,
    TAccountSystemProgram
  >);
}

export type ParsedInitializeVestingInstruction<
  TProgram extends string = typeof DOPPLER_VESTING_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    payer: TAccountMetas[0];
    launch: TAccountMetas[1];
    /** Future launch mint. Its signature prevents another caller from claiming this config PDA. */
    baseMint: TAccountMetas[2];
    baseTokenProgram: TAccountMetas[3];
    vestingConfig: TAccountMetas[4];
    systemProgram: TAccountMetas[5];
  };
  data: InitializeVestingInstructionData;
};

export function parseInitializeVestingInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedInitializeVestingInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 6) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 6,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      payer: getNextAccount(),
      launch: getNextAccount(),
      baseMint: getNextAccount(),
      baseTokenProgram: getNextAccount(),
      vestingConfig: getNextAccount(),
      systemProgram: getNextAccount(),
    },
    data: getInitializeVestingInstructionDataDecoder().decode(instruction.data),
  };
}
